import ClearIcon from '@mui/icons-material/Clear';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback } from 'react';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useAccount } from '../../contexts/AccountContext';
import { saveLibraryFileMetadata, uploadLibraryFile } from '../../services/api';

interface FileTypeMimeMap {
  [extension: string]: string;
}

interface SharedDropzoneProps {
  onFilesUploaded?: () => void;
  onFileUploadError?: (error: string) => void;
  selectedFiles?: File[];
  onRemoveFile?: (fileName: string) => void;
  onFilesSelected?: (files: File[]) => void;
  dropzoneText?: string;
  acceptedFileTypes?: string;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  isLoading?: boolean;
  type?: 'cvs' | 'pastProposals' | 'caseStudiesReports' | 'companyInfoRates' | 'tender_docs';
  scope?: 'user' | 'client';
}

// Mapping of file extensions to MIME types
const fileTypeMimeMap: FileTypeMimeMap = {
  // Documents
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.txt': 'text/plain',
  // Spreadsheets
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // Presentations
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Archives
  '.zip': 'application/zip',
  '.rar': 'application/x-rar-compressed',
};

const SharedDropzone: React.FC<SharedDropzoneProps> = ({
  onFilesUploaded,
  onFileUploadError,
  selectedFiles = [],
  onRemoveFile,
  onFilesSelected,
  dropzoneText = 'Drop files here, or click to select',
  acceptedFileTypes = '.pdf,.doc,.docx,.zip,.rar,.txt,.xls,.xlsx,.ppt,.pptx',
  multiple = true,
  maxFiles = 2000,
  maxSize = 314572800,
  isLoading = false,
  type,
  scope = 'user',
}) => {
  const theme = useTheme();
  const { account: currentUser } = useAccount();
  const [uploading, setUploading] = React.useState(false);

  // Determine if essential auth/client details are missing
  const essentialDetailsMissing = !currentUser?.clientId;

  const onDrop = useCallback(
    async (accepted: File[], rejected: FileRejection[]) => {
      // Always handle rejections first
      if (rejected && rejected.length > 0) {
        const uniqueReasons = new Set<string>();
        for (const r of rejected) {
          for (const e of r.errors) {
            if (e.code === 'file-invalid-type') {
              uniqueReasons.add('Unsupported file type');
            } else if (e.code === 'file-too-large') {
              uniqueReasons.add('File too large');
            } else {
              uniqueReasons.add(e.message);
            }
          }
        }

        const reasons = Array.from(uniqueReasons).join(', ');
        const rejectedNames = rejected.map((r) => r.file.name);
        const maxNamesToShow = 5;
        const namesToShow = rejectedNames.slice(0, maxNamesToShow).join(', ');
        const moreCount = rejectedNames.length - maxNamesToShow;

        let moreText = '';
        if (moreCount > 0) {
          const hiddenFiles = rejectedNames.slice(maxNamesToShow);
          const hiddenExtensions = new Set(
            hiddenFiles.map((name) =>
              name.includes('.') ? name.substring(name.lastIndexOf('.')).toLowerCase() : 'unknown'
            )
          );
          const extList = Array.from(hiddenExtensions).join(', ');
          moreText = ` and ${moreCount} more with extensions: ${extList}`;
        }

        const errorMessage = `Ignored ${rejected.length} file(s) (${namesToShow}${moreText}). Reason: ${reasons}.`;

        if (onFileUploadError) {
          onFileUploadError(errorMessage);
        } else {
          // Fallback to alert if no handler is provided, though the new pattern avoids this.
          alert(errorMessage);
        }

        // If there are no accepted files, return early to avoid errors.
        if (!accepted || accepted.length === 0) {
          return;
        }
      }

      if (essentialDetailsMissing) {
        const errorMessage =
          'User not authenticated or client details missing. Cannot process files.';
        if (onFileUploadError) {
          onFileUploadError(errorMessage);
        }
        return;
      }

      // Handle accepted files only if there were no rejections
      if (accepted && accepted.length > 0) {
        if (type === 'tender_docs') {
          if (onFilesSelected) {
            onFilesSelected(accepted);
          }
          // This case is not used by LibraryPage, but we leave it for other potential uses.
          // It requires onFilesSelected, which we have now re-added.
          // If needed in future, this part should be refactored.
        } else if (type) {
          setUploading(true);
          try {
            // Process uploads in batches of 5 to avoid overwhelming the browser/network
            const CONCURRENCY_LIMIT = 5;
            for (let i = 0; i < accepted.length; i += CONCURRENCY_LIMIT) {
              const batch = accepted.slice(i, i + CONCURRENCY_LIMIT);
              const uploadPromises = batch.map(async (file) => {
                if (
                  !currentUser?.clientId ||
                  !currentUser?.teamId ||
                  !currentUser?.uid ||
                  !currentUser?.actualDefaultBucketName
                ) {
                  throw new Error('Client ID, Team ID, User ID, or Actual Bucket Name is missing.');
                }
                const userFolderPath = `clients/${currentUser.clientId}/teams/${currentUser.teamId}/users/${currentUser.uid}`;
                await uploadLibraryFile({
                  userId: currentUser.uid,
                  clientId: currentUser.clientId,
                  actualBucketName: currentUser.actualDefaultBucketName,
                  userFolderPath: userFolderPath,
                  file,
                  type,
                  scope,
                });
                await saveLibraryFileMetadata({
                  userId: currentUser.uid,
                  clientId: currentUser.clientId,
                  file,
                  type,
                  scope,
                });
                return file;
              });
              await Promise.all(uploadPromises);
            }
            if (onFilesUploaded) {
              onFilesUploaded(); // Notify parent of success
            }
          } catch (err: unknown) {
            const error = err as Error;
            const errorMessage = `Upload failed: ${error.message || 'Please try again.'}`;
            if (onFileUploadError) {
              onFileUploadError(errorMessage);
            }
          } finally {
            setUploading(false);
          }
        } else {
          const errorMessage = "File type category (e.g., 'cvs') is not specified.";
          if (onFileUploadError) {
            onFileUploadError(errorMessage);
          }
        }
      }
    },
    [
      onFilesUploaded,
      onFileUploadError,
      currentUser,
      type,
      essentialDetailsMissing,
      onFilesSelected,
      scope,
    ]
  );

  // Convert extension list to MIME type object
  const getAcceptMapping = useCallback(() => {
    if (!acceptedFileTypes) {
      return undefined;
    }

    // Create an object with MIME types as keys and empty arrays as values
    const acceptMap: { [mimeType: string]: string[] } = {};

    for (const ext of acceptedFileTypes.split(',')) {
      const trimmedExt = ext.trim();
      if (fileTypeMimeMap[trimmedExt]) {
        const mimeType = fileTypeMimeMap[trimmedExt];
        if (!acceptMap[mimeType]) {
          acceptMap[mimeType] = [];
        }
        acceptMap[mimeType].push(trimmedExt);
      } else {
        // If it's already a MIME type, use it directly
        if (trimmedExt.includes('/')) {
          if (!acceptMap[trimmedExt]) {
            acceptMap[trimmedExt] = [];
          }
        } else if (trimmedExt.startsWith('.')) {
          const mimeType = 'application/octet-stream';
          if (!acceptMap[mimeType]) {
            acceptMap[mimeType] = [];
          }
          if (!acceptMap[mimeType].includes(trimmedExt)) {
            acceptMap[mimeType].push(trimmedExt);
          }
        }
      }
    }

    return Object.keys(acceptMap).length > 0 ? acceptMap : undefined;
  }, [acceptedFileTypes]);

  const acceptMapping = getAcceptMapping();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    ...(acceptMapping ? { accept: acceptMapping } : {}),
    multiple,
    maxFiles,
    maxSize,
    disabled: isLoading || uploading || essentialDetailsMissing,
  });

  return (
    <Box
      sx={{
        p: 0,
        border: `2px dashed ${isDragActive ? theme.palette.bidlogiq.brick : theme.palette.greyContrast}`,
        borderRadius: 3,
        background: isDragActive ? theme.palette.bidlogiq.brick : theme.palette.greyLight,
        color: isDragActive ? theme.palette.bidlogiq.black : theme.palette.text.primary,
        transition: 'background-color 0.3s, border-color 0.3s, color 0.3s',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        {...getRootProps()}
        sx={{
          textAlign: 'center',
          cursor: isLoading || uploading || essentialDetailsMissing ? 'default' : 'pointer',
          p: 2,
          outline: 'none',
          background: isDragActive ? theme.palette.bidlogiq.brick : 'transparent',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '120px',
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon
          data-testid="CloudUploadIcon"
          sx={{
            fontSize: 30,
            color: isDragActive ? theme.palette.bidlogiq.white : theme.palette.bidlogiq.brick,
            mb: 1,
            transition: 'color 0.3s',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontSize: '1rem',
            color: isDragActive ? theme.palette.bidlogiq.white : theme.palette.text.primary,
            transition: 'color 0.3s',
            textAlign: 'center',
          }}
        >
          {isDragActive ? 'Release to start upload..' : dropzoneText}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            color: isDragActive ? theme.palette.bidlogiq.white : theme.palette.text.secondary,
            fontStyle: 'italic',
            transition: 'color 0.3s',
            textAlign: 'center',
          }}
        >
          Note: Uploads & conversions may take a few minutes.
        </Typography>
      </Box>

      {selectedFiles.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }} />
          <List
            dense
            sx={{
              maxHeight: 600,
              overflow: 'auto',
              backgroundColor: theme.palette.greyLight,
              borderRadius: 3,
              my: 1,
            }}
          >
            {selectedFiles.map((file) => (
              <ListItem
                key={`${file.name}-${file.size}`}
                secondaryAction={
                  onRemoveFile && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => onRemoveFile(file.name)}
                      disabled={isLoading || uploading || essentialDetailsMissing}
                      sx={{
                        backgroundColor: theme.palette.bidlogiq.rust,
                        color: theme.palette.bidlogiq.white,
                        '&:hover': { color: theme.palette.bidlogiq.brick },
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )
                }
                sx={{
                  backgroundColor: theme.palette.greyLight,
                  mb: 0.5,
                  borderRadius: 3,
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary, fontSize: '0.85rem' }}
                    >
                      {file.name}
                    </Typography>
                  }
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                  slotProps={{
                    secondary: { sx: { color: theme.palette.text.primary } },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {uploading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={22} sx={{ color: theme.palette.bidlogiq.orange, mr: 1 }} />
          <Typography sx={{ color: theme.palette.bidlogiq.brick }}>Uploading...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SharedDropzone;
